import React, { useState, useCallback, useRef, useEffect } from "react";
import { Alert, Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { fetchWithTokenRefresh, getCSRFToken } from "views_shared/app/javascript/api/withTokenRefresh";
import StoryIdeaForm from "./StoryIdeaForm";
import ScheduleStoryIdeaModal from "../ScheduleStoryIdeaModal";

// Full-page wrapper around StoryIdeaForm. Replaces the old modal-based
// "Create Story Idea" flow with a proper Rails page; also used at
// /admin/story_ideas/:id/edit when a story_idea_id is supplied by the
// edit.html.erb (#3121).
//
// Footer actions (#3121):
//   Cancel       — back to index without saving
//   Save Draft   — secondary; persists what we have (title-only
//                  required) without scheduling
//   Schedule     — primary; full validation, then opens the
//                  ScheduleStoryIdeaModal; on time pick, POSTs to the
//                  /schedule endpoint with the saved row's id
//
// The footer is sticky so the actions stay reachable as admins scroll
// the form. On any successful path we navigate to
// /admin/story_ideas?highlight=<id> so the index can flash the saved
// row. Cancel goes there too.
const FOOTER_STYLE = {
  position: "sticky",
  bottom: 0,
  zIndex: 2,
};

function incompleteFormMessage(action) {
  return `Fill out the story idea (title, news hook, topics, marketing persona, experts) to enable ${action}.`;
}

// Gate for the Schedule button (full validation required).
function scheduleDisabledReason({ recipientCount, isValid, busy }) {
  if (busy) return null;
  if (recipientCount === 0) return "Pick recipients first to enable Schedule.";
  if (!isValid) return incompleteFormMessage("Schedule");
  return null;
}

export default function StoryIdeaFormPage({ storyIdeaId }) {
  // Edit-mode hydration. If storyIdeaId is provided we GET the existing
  // row before mounting the form, then pass it as initialStoryIdea so
  // formik picks up its values on mount. Without this, the form mounts
  // with blank state and the admin would see an empty form even on the
  // edit URL.
  const [initialStoryIdea, setInitialStoryIdea] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(Boolean(storyIdeaId));

  useEffect(() => {
    if (!storyIdeaId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchWithTokenRefresh(`/admin/story_ideas/${storyIdeaId}.json`);
        if (!resp.ok) throw new Error(`Failed to load (HTTP ${resp.status})`);
        const json = await resp.json();
        if (!cancelled) {
          setInitialStoryIdea(json);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storyIdeaId]);

  const editing = Boolean(storyIdeaId);
  const pageTitle = editing ? "Edit Story Idea" : "Create Story Idea";

  // Form / footer state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const formRef = useRef();

  // Schedule modal state. When the admin clicks Schedule, we first
  // submit the form (full validation, persists the draft). On save
  // success — detected by intent === "schedule" in handleSuccess —
  // we open the modal with the saved row's id, and the modal's
  // onSchedule fires the POST /schedule call.
  const [scheduleTarget, setScheduleTarget] = useState(null); // saved story idea row, or null
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Navigate back to the index after a successful save. `tab` picks
  // the tab the admin will land on so the just-saved row is visible
  // in context: save-draft → Draft tab, schedule → Scheduled tab.
  // `id` flashes the matching row via the index page's highlight
  // mechanism.
  const navigateToIndex = useCallback((id, tab) => {
    const params = new URLSearchParams();
    if (tab) params.set("tab", tab);
    if (id) params.set("highlight", String(id));
    const query = params.toString();
    window.location.href = `/admin/story_ideas${query ? `?${query}` : ""}`;
  }, []);

  const handleCancel = useCallback(() => {
    window.location.href = "/admin/story_ideas";
  }, []);

  const handleSaveDraft = useCallback(() => {
    setActionError(null);
    formRef.current?.saveAsDraft();
  }, []);

  const handleScheduleClick = useCallback(() => {
    setActionError(null);
    // Trigger full-shape validation + save first. On success
    // (intent === "schedule" in handleSuccess) we open the modal.
    formRef.current?.saveAndScheduleIntent();
  }, []);

  const handleSuccess = useCallback(
    (savedStoryIdea, intent) => {
      if (intent === "save-draft") {
        // Land on the Draft tab so the admin sees their saved row
        // alongside their other in-progress drafts.
        navigateToIndex(savedStoryIdea?.id, "draft");
        return;
      }
      if (intent === "schedule") {
        // Form save complete — open the modal so the admin can pick a
        // time. handleScheduleConfirm navigates to the Scheduled tab
        // on success.
        setScheduleTarget(savedStoryIdea);
        return;
      }
      // Existing intents (close / keep-open) keep their old behaviour.
      if (intent === "keep-open") return; // form soft-reset; stay on page
      navigateToIndex(savedStoryIdea?.id);
    },
    [navigateToIndex],
  );

  const handleScheduleConfirm = useCallback(
    async (params) => {
      if (!scheduleTarget?.id) return;
      setScheduleBusy(true);
      try {
        const resp = await fetchWithTokenRefresh(`/admin/story_ideas/${scheduleTarget.id}/schedule`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify(params),
        });
        if (!resp.ok) {
          let msg = `Schedule failed (HTTP ${resp.status})`;
          try {
            const body = await resp.json();
            msg = body.error || body.errors?.join(", ") || msg;
          } catch (_) {
            // fall through with the default msg
          }
          setActionError(msg);
          return;
        }
        setScheduleTarget(null);
        // After a successful schedule, land on the Scheduled tab so
        // the admin sees their now-scheduled row in context.
        navigateToIndex(scheduleTarget.id, "scheduled");
      } finally {
        setScheduleBusy(false);
      }
    },
    [scheduleTarget, navigateToIndex],
  );

  const scheduleReason = scheduleDisabledReason({ recipientCount, isValid, busy: isSubmitting });
  const scheduleDisabled = isSubmitting || Boolean(scheduleReason);
  // Save Draft is enabled as long as we're not mid-submit. The form's
  // saveAsDraft method enforces the only client-side hard requirement
  // (non-empty title), so we don't need to gate at this level.
  const saveDraftDisabled = isSubmitting;

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
        <div className="mt-2 text-muted">Loading story idea&hellip;</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert variant="danger" className="mt-3">
        Couldn&apos;t load the story idea: {loadError}
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h1 className="h3 mb-0">{pageTitle}</h1>
      </div>

      <StoryIdeaForm
        // Force-remount the form whenever the underlying StoryIdea id
        // changes. StoryIdeaForm's edit-mode hydration runs in a
        // useEffect with an empty deps array (intentional — the
        // initialStoryIdea snapshot is captured once at mount). That's
        // fine for the current full-page-reload navigation flow, but
        // would silently break if anyone wraps this page in a client-
        // side router that swaps storyIdeaId without unmounting. The
        // key prop makes the React-mount lifecycle the authority on
        // id-change handling — defensive and zero-cost.
        key={initialStoryIdea?.id ?? "new"}
        ref={formRef}
        initialStoryIdea={initialStoryIdea}
        onSuccess={handleSuccess}
        onSubmittingChange={setIsSubmitting}
        onRecipientCountChange={setRecipientCount}
        onIsValidChange={setIsValid}
        // onClose is unused on the page (no modal to close); navigation
        // happens in handleSuccess based on intent.
        onClose={() => {}}
      />

      <div className="border-top bg-body-secondary" style={FOOTER_STYLE}>
        {actionError && (
          <Alert variant="danger" className="mb-0 rounded-0 border-0 border-bottom py-2 px-4" role="alert">
            {actionError}
          </Alert>
        )}
        <div className="d-flex gap-2 justify-content-end px-4 py-3">
          <Button variant="link" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleSaveDraft}
            disabled={saveDraftDisabled}
            data-testid="save-draft-button"
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving&hellip;
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk me-1" aria-hidden="true" />
                Save Draft
              </>
            )}
          </Button>
          <ScheduleButtonWithTooltip
            disabled={scheduleDisabled}
            reason={scheduleReason}
            onClick={handleScheduleClick}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>

      <ScheduleStoryIdeaModal
        show={Boolean(scheduleTarget)}
        onHide={() => setScheduleTarget(null)}
        onSchedule={handleScheduleConfirm}
        storyIdeaTitle={scheduleTarget?.title}
        previousPublishAt={scheduleTarget?.previous_publish_at}
        busy={scheduleBusy}
      />
    </div>
  );
}

function ScheduleButtonWithTooltip({ disabled, reason, onClick, isSubmitting }) {
  const button = (
    <Button variant="primary" onClick={onClick} disabled={disabled} data-testid="schedule-button">
      {isSubmitting ? (
        <>
          <Spinner animation="border" size="sm" className="me-1" />
          Saving&hellip;
        </>
      ) : (
        <>
          <i className="fa-solid fa-paper-plane me-1" aria-hidden="true" />
          Schedule&hellip;
        </>
      )}
    </Button>
  );
  if (!reason) return button;
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id="schedule-disabled-reason">{reason}</Tooltip>}>
      <span className="d-inline-block">{React.cloneElement(button, { style: { pointerEvents: "none" } })}</span>
    </OverlayTrigger>
  );
}
